import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { SlidersHorizontal } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { allSearchPageFilterChipsAtom } from '@/pages/Search/SearchPage/atoms/searchPageAtoms';
import { useGetFormattedSearchResultsText } from '@/pages/Search/SearchPage/hooks/useGetFormattedSearchResultsText';
import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import { getAggregationsOnDemandStatus, getSearchUiState } from '@/redux/modules/search/search.selectors';
import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { Distance } from '@/types/Distance';
import { isUnknownDynamicFacet } from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { FilterAction } from '@/types/SearchFacet';
import {
    AllFiltersDrawerClosedReason,
    useTrackAllFiltersDrawerClosed,
    useTrackAllFiltersDrawerOpened,
} from '@/utils/amplitude/analytics';
import {
    DEFAULT_STATUS_FOR_ARCHIVED_SEARCH,
    DEFAULT_STATUS_FOR_ONLINE_SEARCH,
    parseQueryParams,
} from '@/utils/queryParams';

import { CategoryFilter } from '../CategoryFilter/CategoryFilter';
import { KeywordFilter } from '../KeywordFilter/KeywordFilter';
import { LocationFilter } from '../LocationFilter/LocationFilter';
import { ShippingFilter } from '../ShippingFilter/ShippingFilter';
import { SoldOnLiveAuctioneersFilter } from '../SoldOnLiveAuctioneersFilter/SoldOnLiveAuctioneersFilter';
import { SortFilter } from '../SortFilter/SortFilter';
import { AttributeFacetFilter } from './components/AttributeFacetFilter/AttributeFacetFilter';
import { AuctionHouseFilter } from './components/AuctionHouseFilter/AuctionHouseFilter';
import { BuyersPremiumFilter } from './components/BuyersPremiumFilter/BuyersPremiumFilter';
import { BuyingFormatFilter } from './components/BuyingFormatFilter/BuyingFormatFilter';
import { CreatorFilter } from './components/CreatorFilter/CreatorFilter';
import { CurrentBidFilter } from './components/CurrentBidFilter/CurrentBidFilter';
import { MaterialFilter } from './components/MaterialFilter/MaterialFilter';
import { OriginFilter } from './components/OriginFilter/OriginFilter';
import { PriceRangeFilter } from './components/PriceRangeFilter/PriceRangeFilter';
import { StyleAndPeriodFilter } from './components/StyleAndPeriodFilter/StyleAndPeriodFilter';

type Props = {
    currentOpenMenu: CurrentOpenMenu;
    defaultSort?: SearchSortAndDirection;
    distance: Distance;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
    permittedSortOptions: SearchSortAndDirection[];
    setShowAllFiltersDrawer: (show: boolean) => void;
    showAllFiltersDrawer: boolean;
};

export const AllFilters = ({
    currentOpenMenu,
    defaultSort,
    distance,
    isArchivedSearch,
    onMenuClose,
    onMenuOpen,
    permittedSortOptions,
    setShowAllFiltersDrawer,
    showAllFiltersDrawer,
}: Props) => {
    const { formatMessage } = useIntl();
    const isMobile = useWindowIsMobile();
    const dispatch = useAppDispatch();

    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const hasTrackedDrawerEvent = useRef(false);

    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const { submitted: isSearchSubmitted, totalFound } = useAppSelector(getSearchUiState);
    const aggregationsOnDemandStatus = useAppSelector(getAggregationsOnDemandStatus);
    const showAggregationsLoading = aggregationsOnDemandStatus === 'loading';

    const navigateWithParams = useNavigateSearchPageWithParams();

    const trackAllFiltersDrawerClosed = useTrackAllFiltersDrawerClosed();
    const trackAllFiltersDrawerOpened = useTrackAllFiltersDrawerOpened();

    const handleClose = useCallback(
        (reason: 'backdrop' | 'closeButton' | 'escape' | 'external' | 'viewResults') => {
            if (!hasTrackedDrawerEvent.current && reason !== 'external') {
                let reasonForClose: AllFiltersDrawerClosedReason;
                switch (reason) {
                    case 'backdrop':
                        reasonForClose = AllFiltersDrawerClosedReason.Backdrop;
                        break;
                    case 'closeButton':
                        reasonForClose = AllFiltersDrawerClosedReason.CloseButton;
                        break;
                    case 'escape':
                        reasonForClose = AllFiltersDrawerClosedReason.Escape;
                        break;
                    case 'viewResults':
                        reasonForClose = AllFiltersDrawerClosedReason.ViewResults;
                        break;
                    default:
                        break;
                }
                trackAllFiltersDrawerClosed({ reason: reasonForClose });
                hasTrackedDrawerEvent.current = true;
            }

            window.history.replaceState({}, '');
            setShowAllFiltersDrawer(false);
        },
        [setShowAllFiltersDrawer, trackAllFiltersDrawerClosed]
    );

    const onOpenDrawer = useCallback(() => {
        hasTrackedDrawerEvent.current = false;
        dispatch(fetchSearchAggregationsOnDemand());
        trackAllFiltersDrawerOpened();
        setShowAllFiltersDrawer(true);
    }, [dispatch, trackAllFiltersDrawerOpened, setShowAllFiltersDrawer]);

    const onClearAllFilters = useCallback(() => {
        const searchStatus = isArchivedSearch ? DEFAULT_STATUS_FOR_ARCHIVED_SEARCH : DEFAULT_STATUS_FOR_ONLINE_SEARCH;

        const params: {
            house_id?: string;
            keyword: string;
            sort: SearchSortAndDirection;
            status: typeof DEFAULT_STATUS_FOR_ARCHIVED_SEARCH | typeof DEFAULT_STATUS_FOR_ONLINE_SEARCH;
        } = {
            keyword: queryParams.keyword ?? '',
            sort: DEFAULT_SORT_ORDER,
            status: searchStatus,
        };

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: FilterAction.ClearAllFilters,
            params,
        });
    }, [isArchivedSearch, navigateWithParams, queryParams.keyword]);

    const allFilterChips = useAtomValue(allSearchPageFilterChipsAtom);
    const searchFacets = useAppSelector(getSearchFacets);
    const dynamicFacets = useMemo(
        () => searchFacets.filter((facet) => isUnknownDynamicFacet(facet.id) && facet.options?.length > 0),
        [searchFacets]
    );

    const totalFoundText = useGetFormattedSearchResultsText({ totalFound });

    return (
        <div className="xsMax:w-full">
            <Button
                className="hidden w-full !px-xs xsMax:flex"
                iconLeft={SlidersHorizontal}
                onClick={onOpenDrawer}
                variant="outlined"
            >
                {formatMessage({ id: 'searchPage.mobileHeader.title' })}
            </Button>
            <Chip
                className="xsMax:hidden"
                filterCount={allFilterChips.length}
                onClick={onOpenDrawer}
                style="allFilters"
            >
                {formatMessage({ id: 'searchPage.facets.allFilters' })}
            </Chip>
            <Drawer
                actions={
                    <div className="grid w-full grid-cols-2 gap-xs">
                        <Button
                            onClick={onClearAllFilters}
                            variant="outlined"
                        >
                            {formatMessage({ id: 'clear' })}
                        </Button>
                        <Button
                            loading={isSearchSubmitted}
                            onClick={() => handleClose('viewResults')}
                        >
                            {formatMessage({ id: 'searchPage.facets.viewResults' }, { totalFoundText })}
                        </Button>
                    </div>
                }
                // Higher z-index than feedback widget
                anchor="left"
                className="z-[1001]"
                handleClose={(reason) => handleClose(reason)}
                hasShadow
                headerText="All Filters"
                keepInDOMWhileClosed={false}
                show={showAllFiltersDrawer}
                size="wide"
            >
                <div className="flex flex-col">
                    {showAggregationsLoading ? (
                        <Loading />
                    ) : (
                        <>
                            {isMobile && (
                                <SortFilter
                                    defaultSort={defaultSort}
                                    isAllFilters
                                    permittedSortOptions={permittedSortOptions}
                                />
                            )}
                            <KeywordFilter isAllFilters />
                            <CategoryFilter
                                currentOpenMenu={currentOpenMenu}
                                isAllFilters
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <LocationFilter
                                currentOpenMenu={currentOpenMenu}
                                distance={distance}
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <ShippingFilter
                                currentOpenMenu={currentOpenMenu}
                                isAllFilters
                                isArchivedSearch={isArchivedSearch}
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            <AuctionHouseFilter
                                currentOpenMenu={currentOpenMenu}
                                isAllFilters
                                onMenuClose={onMenuClose}
                                onMenuOpen={onMenuOpen}
                            />
                            {isArchivedSearch && isLoggedIn && <SoldOnLiveAuctioneersFilter isAllFilters />}
                            {!isArchivedSearch && <BuyersPremiumFilter />}
                            {!isArchivedSearch && <BuyingFormatFilter />}
                            <CurrentBidFilter isArchivedSearch={isArchivedSearch} />
                            <PriceRangeFilter />
                            <OriginFilter />
                            <MaterialFilter />
                            <StyleAndPeriodFilter />
                            <CreatorFilter />
                            {dynamicFacets.map((facet) => (
                                <AttributeFacetFilter
                                    facetId={facet.id}
                                    key={facet.id}
                                    mergedOptionFacet={facet}
                                    title={facet.name}
                                />
                            ))}
                        </>
                    )}
                </div>
            </Drawer>
        </div>
    );
};
